import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Nav from "../Components/Nav/Nav";
import Login from "./Login";
import AdminMenu from "../Components/Admin/AdminMenu";
import AdminFormHeader from "../Components/Admin/AdminFormHeader";

import {
  getChatbotQA,
  addChatbotQA,
  updateChatbotQA,
  deleteChatbotQA,
  getQuestionsEnAttente,
  repondreQuestionEnAttente,
  deleteQuestionEnAttente,
  getChatbotStats,
} from "../actions/chatbot.actions";
import { getUsers } from "../actions/users.actions";
import ChatbotStats from "../Components/Admin/ChatbotStats";

import { useTable, usePagination } from "react-table";
import { LayoutDashboard, Database, Hourglass, PlusCircle, Plus } from "lucide-react";

// Composant pour les cellules d'action
const ActionCell = ({ value, row, onDelete, onEdit }) => (
  <div className="flexTable">
    <button onClick={() => onEdit(value, row.original)} className="blackButtonTable">
      Modifier
    </button>
    <button onClick={() => onDelete(value)} className="deleteButton">
      Supprimer
    </button>
  </div>
);

// Définition des colonnes du tableau
const getColumns = (handleDelete, handleEdit) => [
  {
    Header: "Question",
    accessor: "question",
  },
  {
    Header: "Réponse",
    accessor: "reponse",
    Cell: ({ value }) => (
      <span className="cell-clamp" title={value}>
        {value}
      </span>
    ),
  },
  {
    Header: "Catégorie",
    accessor: "category",
    Cell: ({ value }) => value || "Général"
  },
  {
    Header: "Actions",
    accessor: "id",
    Cell: ({ cell, row }) => (
      <ActionCell value={cell.value} row={row} onDelete={handleDelete} onEdit={handleEdit} />
    ),
  },
];

const AdminChatbotQA = () => {
  // --- Redux state ----------------------------------------------------------
  const userData = useSelector((state) => state.userReducer);
  const qaData = useSelector((state) => state.chatbotReducer.chatbotQA);
  const questionsEnAttente = useSelector(
    (state) => state.chatbotReducer.questionsEnAttente
  );
  const chatbotStats = useSelector((state) => state.chatbotReducer.stats);
  const users = useSelector((state) => state.usersReducer);

  // --- Local state (form) ----------------------------------------------------
  const [question, setQuestion] = useState("");
  const [reponse, setReponse] = useState("");
  const [category, setCategory] = useState("Technique");
  const [reponseEnAttente, setReponseEnAttente] = useState({});
  const [categoryEnAttente, setCategoryEnAttente] = useState({});
  const [showReponseForm, setShowReponseForm] = useState({});
  
  // Tabs: 'qa', 'stats', 'pending'
  const [activeTab, setActiveTab] = useState('stats');

  // État pour l'édition
  const [editingId, setEditingId] = useState(null);
  const [editQuestion, setEditQuestion] = useState("");
  const [editReponse, setEditReponse] = useState("");
  const [editCategory, setEditCategory] = useState("Général");
  
  // État pour la recherche
  const [searchTerm, setSearchTerm] = useState("");

  const dispatch = useDispatch();
  // --- CRUD ------------------------------------------------------------------
  const handleSubmit = (e) => {
    e.preventDefault();

    dispatch(addChatbotQA(question, reponse, category)).then(() =>
      dispatch(getChatbotQA())
    );

    setQuestion("");
    setReponse("");
    setCategory("Général");
  };
  const handleDelete = (id) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette entrée ?")) {
      dispatch(deleteChatbotQA(id)).then(() => dispatch(getChatbotQA()));
    }
  };

  // Fonction pour gérer l'édition
  const handleEdit = (id, qaData) => {
    setEditingId(id);
    setEditQuestion(qaData.question);
    setEditReponse(qaData.reponse);
    setEditCategory(qaData.category || "Général");
  };

  // Fonction pour sauvegarder l'édition
  const handleSaveEdit = () => {
    if (editQuestion.trim() && editReponse.trim()) {
      dispatch(updateChatbotQA(editingId, editQuestion, editReponse, editCategory)).then(() => {
        dispatch(getChatbotQA());
        setEditingId(null);
        setEditQuestion("");
        setEditReponse("");
        setEditCategory("Général");
      });
    }
  };

  // Fonction pour annuler l'édition
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditQuestion("");
    setEditReponse("");
    setEditCategory("Général");
  };

  // Gestion des questions en attente
  const handleRepondreQuestion = (questionId, reponse) => {
    const cat = categoryEnAttente[questionId] || "Général";
    if (reponse.trim()) {
      dispatch(repondreQuestionEnAttente(questionId, reponse, cat)).then(() => {
        dispatch(getQuestionsEnAttente());
        dispatch(getChatbotQA()); // Refresh aussi les QA pour voir la nouvelle entrée
      });
    }
  };

  const handleDeleteQuestionEnAttente = (id) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette question ?")) {
      dispatch(deleteQuestionEnAttente(id)).then(() =>
        dispatch(getQuestionsEnAttente())
      );
    }
  };
  // --- Table columns ---------------------------------------------------------
  const columns = useMemo(() => getColumns(handleDelete, handleEdit), []);

  const data = useMemo(() => (Array.isArray(qaData) ? qaData : []), [qaData]);

  // Données filtrées pour la recherche
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) {
      return data;
    }
    
    const searchLower = searchTerm.toLowerCase();
    return data.filter(item => 
      item.question.toLowerCase().includes(searchLower) ||
      item.reponse.toLowerCase().includes(searchLower) ||
      (item.category && item.category.toLowerCase().includes(searchLower))
    );
  }, [data, searchTerm]);

  // --- react-table pagination ------------------------------------------------
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state,
    gotoPage,
    setPageSize,
    prepareRow,
  } = useTable(
    { columns, data: filteredData, initialState: { pageIndex: 0, pageSize: 5 } },
    usePagination
  );

  const { pageIndex, pageSize } = state;
  // --- Fetch on mount --------------------------------------------------------
  useEffect(() => {
    dispatch(getChatbotQA());
    dispatch(getQuestionsEnAttente());
    dispatch(getChatbotStats());
    dispatch(getUsers());
  }, [dispatch]);

  // --------------------------------------------------------------------------
  return userData.isAdmin || userData.isReferent ? (
    <>
      <Nav />
      <div className="mainContainer">
        <AdminMenu />

        <div className="centerBlock_admin">
          <AdminFormHeader
            title="Administration du Chatbot"
            subtitle="Gérez la base de questions / réponses, les statistiques et les demandes en attente."
          />

          {/* Onglets de navigation */}
          <div className="chatbot-tabs">
            {[
              { key: "stats", label: "Tableau de bord", icon: <LayoutDashboard size={16} /> },
              { key: "qa", label: "Questions / Réponses", icon: <Database size={16} /> },
              {
                key: "pending",
                label: `En attente (${questionsEnAttente ? questionsEnAttente.length : 0})`,
                icon: <Hourglass size={16} />,
              },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`chatbot-tab ${activeTab === tab.key ? "active" : ""}`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          {/* Contenu des Onglets */}
          
          {/* --- STATS TAB --- */}
          {activeTab === 'stats' && (
             <ChatbotStats stats={chatbotStats} users={users} />
          )}

          {/* --- Q/A TAB --- */}
          {activeTab === 'qa' && (
            <>
              {/* ---------- Formulaire ---------- */}
              <h2 className="admin-form-title">
                <PlusCircle size={20} /> Nouvelle question / réponse
              </h2>

              <form className="admin-form" onSubmit={handleSubmit}>
                <div className="admin-form-field">
                  <label htmlFor="question">Question</label>
                  <input
                    id="question"
                    type="text"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    required
                    placeholder="Posez la question ici..."
                  />
                </div>

                <div className="admin-form-field">
                  <label htmlFor="category">Catégorie</label>
                  <select
                    id="category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    <option value="Technique">Technique</option>
                    <option value="E-commerce">E-commerce</option>
                    <option value="Boutique">Boutique</option>
                    <option value="Webtool">Webtool</option>
                    <option value="Mail / NDD">Mail / NDD</option>
                  </select>
                </div>

                <div className="admin-form-field">
                  <label htmlFor="reponse">Réponse</label>
                  <textarea
                    id="reponse"
                    rows="6"
                    value={reponse}
                    onChange={(e) => setReponse(e.target.value)}
                    required
                    placeholder="La réponse du chatbot..."
                  />
                </div>

                <div className="admin-form-actions">
                  <button type="submit">
                    <Plus size={17} /> Ajouter la réponse
                  </button>
                </div>
              </form>

              {/* ---------- Tableau des Q&A existantes ---------- */}
              <div className="allQA">
                <h2>Questions et Réponses existantes</h2>
                
                {/* Barre de recherche */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ position: 'relative', maxWidth: '400px' }}>
                    <i 
                      className="fa-solid fa-search" 
                      style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#ffffff',
                        zIndex: 1
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Rechercher..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px 12px 12px 40px',
                        backgroundColor: '#414142',
                        color: '#ffffff',
                        border: '1px solid #606061',
                        borderRadius: '6px',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'border-color 0.3s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#007bff'}
                      onBlur={(e) => e.target.style.borderColor = '#606061'}
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        style={{
                          position: 'absolute',
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          backgroundColor: 'transparent',
                          border: 'none',
                          color: '#ffffff',
                          cursor: 'pointer',
                          padding: '4px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <i className="fa-solid fa-times" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="admin-table-wrap">
                  <table {...getTableProps()}>
                    <thead>
                      {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                          {headerGroup.headers.map((column) => (
                            <th {...column.getHeaderProps()}>
                              {column.render("Header")}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>

                    <tbody {...getTableBodyProps()}>
                      {page.map((row) => {
                        prepareRow(row);
                        return (
                          <tr {...row.getRowProps()}>
                            {row.cells.map((cell) => (
                              <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* ---------- Pagination ---------- */}
                <div className="pagination">
                  <button onClick={previousPage} disabled={!canPreviousPage}>
                    Précédent
                  </button>
                  <button onClick={nextPage} disabled={!canNextPage}>
                    Suivant
                  </button>

                  <span>
                    Page{" "}
                    <strong>
                      {pageIndex + 1} sur {pageOptions.length}
                    </strong>
                  </span>

                  <span>
                    Aller à :{" "}
                    <input
                      type="number"
                      defaultValue={pageIndex + 1}
                      onChange={(e) => {
                        const n = e.target.value ? Number(e.target.value) - 1 : 0;
                        gotoPage(n);
                      }}
                      style={{ width: "50px" }}
                    />
                  </span>

                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                  >
                    {[5, 10, 20].map((size) => (
                      <option key={size} value={size}>
                        Afficher {size}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}

          {/* --- PENDING TAB --- */}
          {activeTab === 'pending' && (
            <div className="questionsEnAttente" style={{ marginTop: "3rem" }}>
              <h2>Questions en Attente de Réponse</h2>

              {questionsEnAttente && questionsEnAttente.length > 0 ? (
                <div className="questionsList">
                  {questionsEnAttente.map((questionObj) => (
                    <div
                      key={questionObj.id}
                      className="questionCard"
                      style={{
                        border: "1px solid #404040",
                        padding: "1rem",
                        margin: "1rem 0",
                        borderRadius: "8px",
                        backgroundColor: "#2d2d2d",
                        color: "#e5e7eb"
                      }}
                    >
                      <div
                        className="questionHeader"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "1rem",
                        }}
                      >
                        <h3 style={{ margin: 0, color: "#ffffff" }}>
                          Question non résolue
                        </h3>
                        <div>
                          <button
                            className="ds-btn ds-btn--ghost"
                            style={{ marginRight: "0.5rem" }}
                            onClick={() =>
                              setShowReponseForm({
                                ...showReponseForm,
                                [questionObj.id]:
                                  !showReponseForm[questionObj.id],
                              })
                            }
                          >
                            {showReponseForm[questionObj.id]
                              ? "Annuler"
                              : "Répondre"}
                          </button>
                          <button
                            className="ds-btn ds-btn--danger"
                            onClick={() =>
                              handleDeleteQuestionEnAttente(questionObj.id)
                            }
                          >
                            Supprimer
                          </button>
                        </div>
                      </div>

                      <div
                        className="questionText"
                        style={{
                          backgroundColor: "#374151",
                          color: "#e5e7eb",
                          padding: "1rem",
                          borderRadius: "4px",
                          marginBottom: "1rem",
                          fontStyle: "italic",
                        }}
                      >
                        <strong>Question posée :</strong> "{questionObj.question}"
                      </div>

                      <div
                        className="questionMeta"
                        style={{
                          fontSize: "0.9rem",
                          color: "#9ca3af",
                          marginBottom: "1rem",
                        }}
                      >
                        <span>
                          Statut : <strong>{questionObj.statut}</strong>
                        </span>
                        <span style={{ marginLeft: "1rem" }}>
                          Posée le :{" "}
                          <strong>
                            {new Date(questionObj.createdAt).toLocaleDateString(
                              "fr-FR"
                            )}
                          </strong>
                        </span>
                        {(questionObj.name || questionObj.firstname) && (
                          <span style={{ marginLeft: "1rem" }}>
                            <i
                              className="fa-solid fa-user"
                              style={{ color: "#007bff", marginRight: "0.3rem" }}
                            ></i>
                            Demandeur :{" "}
                            <strong>
                              {questionObj.firstname} {questionObj.name}
                            </strong>
                          </span>
                        )}
                        {!questionObj.name && !questionObj.firstname && (
                          <span
                            style={{
                              marginLeft: "1rem",
                              color: "#999",
                              fontStyle: "italic",
                            }}
                          >
                            <i
                              className="fa-solid fa-user-secret"
                              style={{ color: "#999", marginRight: "0.3rem" }}
                            ></i>
                            Demandeur : <strong>Anonyme</strong>
                          </span>
                        )}
                      </div>

                      {showReponseForm[questionObj.id] && (
                        <div
                          className="reponseForm"
                          style={{
                            backgroundColor: "#2d2d2d",
                            padding: "1rem",
                            borderRadius: "4px",
                            border: "1px solid #404040",
                          }}
                        >
                          <h4>Ajouter une réponse :</h4>
                          
                          <div style={{ marginBottom: '10px' }}>
                             <label style={{display: 'block', marginBottom: '5px', fontWeight: '500', color: '#d1d5db'}}>Catégorie :</label>
                             <select
                                value={categoryEnAttente[questionObj.id] || "Technique"}
                                onChange={(e) =>
                                  setCategoryEnAttente({
                                    ...categoryEnAttente,
                                    [questionObj.id]: e.target.value,
                                  })
                                }
                                style={{
                                  width: "100%",
                                  padding: "0.5rem",
                                  border: "1px solid #404040",
                                  borderRadius: "4px",
                                  backgroundColor: "#1f2937",
                                  color: "#ffffff"
                                }}
                             >
                                 <option value="Technique">Technique</option>
                                 <option value="E-commerce">E-commerce</option>
                                 <option value="Boutique">Boutique</option>
                                 <option value="Webtool">Webtool</option>
                                 <option value="Mail / NDD">Mail / NDD</option>
                             </select>
                          </div>

                          <textarea
                            value={reponseEnAttente[questionObj.id] || ""}
                            onChange={(e) =>
                              setReponseEnAttente({
                                ...reponseEnAttente,
                                [questionObj.id]: e.target.value,
                                
                              })
                            }
                            placeholder="Tapez votre réponse ici..."
                            style={{
                              width: "100%",
                              minHeight: "100px",
                              padding: "0.5rem",
                              border: "1px solid #404040",
                              borderRadius: "4px",
                              resize: "vertical",
                              backgroundColor: "#1f2937",
                              color: "#ffffff"
                            }}
                          />
                          <div style={{ marginTop: "1rem" }}>
                            <button
                              className="ds-btn ds-btn--primary"
                              style={{ marginRight: "0.5rem" }}
                              onClick={() => {
                                handleRepondreQuestion(
                                  questionObj.id,
                                  reponseEnAttente[questionObj.id]
                                );
                                setReponseEnAttente({
                                  ...reponseEnAttente,
                                  [questionObj.id]: "",
                                });
                                // Reset category
                                setCategoryEnAttente({
                                    ...categoryEnAttente,
                                    [questionObj.id]: "Général"
                                });
                                setShowReponseForm({
                                  ...showReponseForm,
                                  [questionObj.id]: false,
                                });
                              }}
                              disabled={!reponseEnAttente[questionObj.id]?.trim()}
                            >
                              Valider la réponse
                            </button>
                            <button
                              className="ds-btn ds-btn--ghost"
                              onClick={() => {
                                setReponseEnAttente({
                                  ...reponseEnAttente,
                                  [questionObj.id]: "",
                                });
                                setShowReponseForm({
                                  ...showReponseForm,
                                  [questionObj.id]: false,
                                });
                              }}
                            >
                              Annuler
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "2rem",
                    color: "#666",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <i
                    className="fa-solid fa-check-circle"
                    style={{
                      fontSize: "2rem",
                      marginBottom: "1rem",
                      color: "#28a745",
                    }}
                  ></i>
                  <p>
                    Aucune question en attente ! Toutes les questions ont été
                    traitées.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Modal d'édition */}
          {editingId && (
            <div 
              className="editModal"
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
              }}
            >
              <div 
                style={{
                  backgroundColor: 'white',
                  padding: '2rem',
                  borderRadius: '8px',
                  width: '90%',
                  maxWidth: '600px',
                  maxHeight: '80vh',
                  overflow: 'auto'
                }}
              >
                <h3>Éditer la Question/Réponse</h3>
                
                <div style={{ marginBottom: '1rem' }}>
                  <label htmlFor="editQuestion">Question :</label>
                  <input
                    id="editQuestion"
                    type="text"
                    value={editQuestion}
                    onChange={(e) => setEditQuestion(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      marginTop: '0.5rem'
                    }}
                    required
                  />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label htmlFor="editCategory">Catégorie :</label>
                  <select
                    id="editCategory"
                    value={editCategory}
                    onChange={(e) => setEditCategory(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      marginTop: '0.5rem'
                    }}
                  >
                     <option value="Technique">Technique</option>
                     <option value="E-commerce">E-commerce</option>
                     <option value="Boutique">Boutique</option>
                     <option value="Webtool">Webtool</option>
                     <option value="Mail / NDD">Mail / NDD</option>
                  </select>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label htmlFor="editReponse">Réponse :</label>
                  <textarea
                    id="editReponse"
                    value={editReponse}
                    onChange={(e) => setEditReponse(e.target.value)}
                    style={{
                      width: '100%',
                      minHeight: '150px',
                      padding: '0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      marginTop: '0.5rem',
                      resize: 'vertical'
                    }}
                    required
                  />
                </div>

                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <button
                    onClick={handleCancelEdit}
                    style={{
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    disabled={!editQuestion.trim() || !editReponse.trim()}
                    style={{
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '4px',
                      cursor: editQuestion.trim() && editReponse.trim() ? 'pointer' : 'not-allowed',
                      opacity: editQuestion.trim() && editReponse.trim() ? 1 : 0.6
                    }}
                  >
                    Sauvegarder
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  ) : (
    <Login />
  );
};


export default AdminChatbotQA;
